import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Badge, Breadcrumb, Col } from 'antd';
import styled from 'styled-components';

import { LeftOutlined, LoadingOutlined } from '@ant-design/icons';
import { useClientEnvironment } from '@hooks/clientEnvironment';
import { getJobsBoardLayout } from '@layouts/JobsBoardLayout';
import { listMemberships } from '@lib/api/endpoints/invites';
import { UK_COUNTRY } from '@lib/utils/constants';
import { NextPageWithLayout } from '@lib/utils/pageHelper';
import {
  AVAIL_CHECK_STATUS_TO_LABEL_MAP,
  BACKEND_GENERATED_MESSAGE_TYPES,
  checkJobPageStatus,
} from '@lib/utils/statuses';
import theme from '@styles/theme';
import { CastingDirector } from '@views/jobs/components/CastingDirector';
import { Footer } from '@views/jobs/components/Footer';
import { RoleInfo } from '@views/jobs/components/RoleInfo';
import { StyledForm, StyledFormContainer } from '@views/jobs/styles';

import BoxData from './Box';

const JobsIndex: NextPageWithLayout = () => {
  const [flagFromChild, setFlagFromChild] = useState(false);
  const [MembershipDetails, setMembershipDetails] =
    useState<SubmissionDetails | SubmissionDetailsMisc>();

  const isUkProject =
    MembershipDetails?.project_location_country === UK_COUNTRY;

  const { data: clientEnvironment } = useClientEnvironment();
  const { isEveryset } = clientEnvironment || {};

  const router = useRouter();
  const { list_membership_id, notification_id } = router.query;
  const [loading, setLoading] = useState(true);

  const isMobileDevice = window.innerWidth <= 768;
  useEffect(() => {
    const getMembershipDetails = async () => {
      if (notification_id) {
        try {
          const apiMembershipDetails =
            await listMemberships.getListMembershipMisc(
              '' + list_membership_id,
              typeof notification_id === 'string' && notification_id
            );
          setMembershipDetails(apiMembershipDetails);
          setLoading(false);
        } catch (error) {
          console.error('Error while API call:', error);
          setLoading(false);
        }
      } else {
        try {
          const apiMembershipDetails = await listMemberships.getListMembership(
            '' + list_membership_id
          );
          setMembershipDetails(apiMembershipDetails);
          setLoading(false);
        } catch (error) {
          console.error('Error while API call:', error);
          setLoading(false);
        }
      }
    };
    getMembershipDetails();
  }, [flagFromChild, list_membership_id, notification_id]);

  const handleFlagChange = (flagValue) => {
    setFlagFromChild(flagValue);
  };

  const subtitle = MembershipDetails?.project_type;

  if (loading) {
    return (
      <LoadingOutlined
        style={{
          fontSize: 24,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          color: theme.palette.primary[0],
        }}
        spin
      />
    );
  }

  const membershipStatus = MembershipDetails?.list_status;
  checkJobPageStatus(membershipStatus, 'invited', router);

  const castingInfo = MembershipDetails?.casting_info;
  if (isEveryset && isUkProject) {
    castingInfo.casting_contact = 'Everyset UK Casting';
  }

  return (
    <>
      <StyleWebDiv>
        <div
          style={{
            display: 'flex',
            height: '50px',
            boxShadow: '0px 25px 10px 1px rgba(64, 64, 64, 0.25)',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Link
            style={{
              height: '26.4px',
              color: 'black',
              display: 'flex',
              alignItems: 'center',
              marginLeft: '56px',
            }}
            href={'/jobs/AvailChecks'}
          >
            <span style={{ textDecoration: 'none' }}>
              <LeftOutlined
                style={{ marginLeft: '10px', width: '26.4px', height: '24px' }}
              />
            </span>
          </Link>
          <StyleTitle style={{ textAlign: 'center', flex: 1 }}>
            {MembershipDetails?.project_name}
          </StyleTitle>
        </div>
      </StyleWebDiv>
      <StyledFormContainer isMobileDevice={isMobileDevice}>
        <StyledForm>
          <div>
            <div>
              {!isMobileDevice && (
                <StyledBreadcrumb separator='>'>
                  <Breadcrumb.Item>
                    <Link href='/jobs/AvailChecks/' passHref>
                      <a
                        style={{
                          color: theme.palette.primary[0],
                          fontWeight: 600,
                        }}
                      >
                        Avail Checks
                      </a>
                    </Link>
                  </Breadcrumb.Item>
                  <Breadcrumb.Item>
                    {MembershipDetails?.project_name}
                  </Breadcrumb.Item>
                </StyledBreadcrumb>
              )}
            </div>
            <Col>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '20px',
                }}
              >
                <div>
                  {!isMobileDevice && (
                    <StyledProjectName className='jobName'>
                      {MembershipDetails?.project_name}
                    </StyledProjectName>
                  )}
                  <Badge
                    className='Badge'
                    style={{
                      height: '23px',
                      borderRadius: '4px',
                      fontSize: isMobileDevice ? '14px' : '12px',
                      marginBottom: '12px',
                      marginLeft: '10px',
                      lineHeight: '19px',
                      display: 'flex',
                      justifyContent: 'center',
                      flexDirection: 'column',
                      color:
                        membershipStatus === 'submitted' ||
                        membershipStatus === 'unavailable' ||
                        membershipStatus === 'released'
                          ? 'white'
                          : 'black',
                      backgroundColor:
                        membershipStatus === 'invited'
                          ? theme.palette.statuses[0]
                          : membershipStatus === 'submitted'
                          ? theme.palette.statuses[1]
                          : membershipStatus === 'unavailable'
                          ? theme.palette.statuses[2]
                          : membershipStatus === 'released'
                          ? theme.palette.statuses[3]
                          : theme.palette.statuses[8],
                    }}
                    count={
                      AVAIL_CHECK_STATUS_TO_LABEL_MAP[membershipStatus] ||
                      membershipStatus
                    }
                  />

                  <div
                    className='Address'
                    style={{ paddingLeft: isMobileDevice ? '15px' : '0px' }}
                  >
                    {subtitle}
                  </div>
                </div>
                <div style={{ marginRight: isMobileDevice ? '20px' : '0px' }}>
                  <CastingDirector castingInfo={castingInfo} />
                </div>
              </div>
              {isMobileDevice ? (
                <div
                  style={{
                    backgroundColor: '#fff',
                  }}
                >
                  {MembershipDetails && (
                    <BoxData
                      props={MembershipDetails}
                      onFlagChange={handleFlagChange}
                    />
                  )}
                </div>
              ) : (
                MembershipDetails && (
                  <BoxData
                    props={MembershipDetails}
                    onFlagChange={handleFlagChange}
                  />
                )
              )}
              {notification_id ||
              BACKEND_GENERATED_MESSAGE_TYPES.includes(membershipStatus)
                ? null
                : MembershipDetails && <RoleInfo data={MembershipDetails} />}
              <Footer data={MembershipDetails} />
            </Col>
          </div>
        </StyledForm>
      </StyledFormContainer>
    </>
  );
};

JobsIndex.getLayout = getJobsBoardLayout;

export default JobsIndex;
const StyleWebDiv = styled.div`
  @media (min-width: 577px) {
    display: none;
  }
`;
const StyleTitle = styled.div`
  font-family: 'Inter';
  font-style: normal;
  font-weight: 600;
  font-size: 22px;
  line-height: 27px;
  color: #333333;
  @media (max-width: 576px) {
    font-size: 17px;
  }
`;
const StyledProjectName = styled.span`
  font-family: Inter;
  font-size: 22px;
  font-weight: 600;
  line-height: 27px;
  letter-spacing: 0em;
  text-align: left;
  color: #202020;
`;
const StyledBreadcrumb = styled(Breadcrumb)`
  color: #000000;
  font-size: ${(props) => props.theme.palette.textFontSize[7]};
  .ant-breadcrumb-separator {
    color: black; /* Replace 'red' with the desired color */
  }
`;
